import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext } from "react-beautiful-dnd";

import CloseIcon from "@mui/icons-material/Close";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import { DatabaseContext, UserContext } from "../../context";
import { shouldFileBeVisible } from "../../utility";
import FinderColumn from "../files/viewing/finderPage/FinderColumn";

const FAVOURITES_KEY = "faves_fggh2973jgbvbiu";

function buildAccessibleFolderTree(folders, files, groups, currentUserData) {
    const folderByKey = new Map(
        folders.map((folder) => [
            folder.key,
            {
                ...folder,
                id: folder.key,
                parentNode: {},
                folders: [],
                ancestors: folder.ancestors || [],
                managers: folder.managers || [],
                users: folder.users || [],
            },
        ]),
    );
    const accessibleFolderKeys = new Set();

    folderByKey.forEach((folder) => {
        const userCanSeeFolder =
            folder.managers.includes(currentUserData.key) ||
            currentUserData.role === "System Digital Manager" ||
            (folder.hub && folder.users.includes(currentUserData.key)) ||
            folder.createdBy === currentUserData.key;

        if (userCanSeeFolder) {
            accessibleFolderKeys.add(folder.key);
            folder.ancestors.forEach((key) => accessibleFolderKeys.add(key));
        }
    });

    files.forEach((file) => {
        if (shouldFileBeVisible(file, groups, currentUserData)) {
            (file.ancestors || []).forEach((key) =>
                accessibleFolderKeys.add(key),
            );
        }
    });

    const accessibleFolders = Array.from(accessibleFolderKeys)
        .map((key) => folderByKey.get(key))
        .filter(Boolean)
        .sort((first, second) =>
            (first.name || "").localeCompare(second.name || ""),
        );
    const accessibleByKey = new Map(
        accessibleFolders.map((folder) => [folder.key, folder]),
    );
    const rootFolders = [];

    accessibleFolders.forEach((folder) => {
        const parentFolder = accessibleByKey.get(folder.parent);
        if (folder.parent === "Root" || !parentFolder) {
            rootFolders.push(folder);
            return;
        }

        parentFolder.folders.push(folder);
        folder.parentNode = parentFolder;
    });

    rootFolders.unshift({
        id: FAVOURITES_KEY,
        key: FAVOURITES_KEY,
        name: "Favourites",
        parent: "Root",
        ancestors: [],
        folders: [],
        files: [],
        hub: false,
        parentNode: {},
        users: [],
        managers: [],
    });

    return rootFolders;
}

export default function ProjectSelectFileDialog(props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        files = [],
        filesLoaded = false,
        folders = [],
        foldersLoaded = false,
        groups = [],
        groupsLoaded = false,
    } = useContext(DatabaseContext);
    const { currentUserData } = useContext(UserContext);

    const [columns, setColumns] = useState([]);
    const [selectedColumnIndex, setSelectedColumnIndex] = useState(-1);
    const scrollRef = useRef(null);

    const isLoading =
        !currentUserData ||
        !filesLoaded ||
        !foldersLoaded ||
        !groupsLoaded;

    const visibleFiles = useMemo(() => {
        if (!currentUserData || isLoading) {
            return [];
        }

        return files.filter((file) =>
            shouldFileBeVisible(file, groups, currentUserData),
        );
    }, [currentUserData, files, groups, isLoading]);

    useEffect(() => {
        if (!props.open || isLoading || !currentUserData) {
            setColumns([]);
            setSelectedColumnIndex(-1);
            return;
        }

        const rootFolders = buildAccessibleFolderTree(
            folders,
            files,
            groups,
            currentUserData,
        );
        setColumns([
            {
                id: "Root",
                name: "Everything",
                folders: rootFolders,
                files: visibleFiles.filter((file) => file.folder === "Root"),
                folder: { name: "Root", id: "root" },
            },
        ]);
        setSelectedColumnIndex(-1);
    }, [
        currentUserData,
        files,
        folders,
        groups,
        isLoading,
        props.open,
        visibleFiles,
    ]);

    function handleFolderSelect(columnIndex, folderIndex) {
        const selectedFolder = columns[columnIndex]?.folders[folderIndex];
        if (!selectedFolder) {
            return;
        }

        const nextColumns = columns.slice(0, columnIndex + 1);
        nextColumns[columnIndex] = {
            ...nextColumns[columnIndex],
            selectedFolderIndex: folderIndex,
        };
        nextColumns.push({
            id: selectedFolder.id,
            name: selectedFolder.name,
            folders: selectedFolder.folders || [],
            files:
                selectedFolder.id === FAVOURITES_KEY
                    ? []
                    : visibleFiles.filter(
                          (file) => file.folder === selectedFolder.id,
                      ),
            folder: selectedFolder,
        });

        setSelectedColumnIndex(columnIndex);
        setColumns(nextColumns);

        window.requestAnimationFrame(() => {
            scrollRef.current?.scrollTo({
                left: scrollRef.current.scrollWidth,
                behavior: "smooth",
            });
        });
    }

    const hasAccessibleContent =
        columns[0]?.folders?.length > 1 || columns[0]?.files?.length > 0;

    return (
        <Dialog
            open={props.open}
            onClose={() => props.onClose()}
            fullScreen={isMobile}
            fullWidth
            maxWidth="xl"
            aria-labelledby="select-file-dialog-title"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: { xs: 0, sm: 3 },
                        overflow: "hidden",
                        height: { xs: "100%", sm: "min(780px, 92vh)" },
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 2,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <FolderOpenOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="select-file-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        {props.title || "Select a file"}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {props.description ||
                            "Browse your available folders and choose a file."}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close file picker"
                    onClick={() => props.onClose()}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    minHeight: 0,
                    p: { xs: 1, sm: 2 },
                    display: "flex",
                    flex: 1,
                    backgroundColor: "background.default",
                }}
            >
                {isLoading ? (
                    <Box
                        role="status"
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "text.secondary",
                        }}
                    >
                        <CircularProgress
                            size={32}
                            aria-label="Loading files"
                        />
                        <Typography variant="body2" sx={{ mt: 1.5 }}>
                            Loading your files…
                        </Typography>
                    </Box>
                ) : !hasAccessibleContent ? (
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 3,
                            textAlign: "center",
                            color: "text.secondary",
                        }}
                    >
                        <FolderOffOutlinedIcon
                            sx={{ mb: 1.25, fontSize: 44, opacity: 0.4 }}
                        />
                        <Typography variant="h6" color="text.primary">
                            No files available
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Files shared with you will appear here.
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        ref={scrollRef}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            minHeight: 0,
                            display: "flex",
                            overflowX: "auto",
                            overflowY: "hidden",
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                            scrollSnapType: { xs: "x mandatory", sm: "none" },
                        }}
                    >
                        <DragDropContext onDragEnd={() => {}}>
                            {columns.map((column, index) => (
                                <Box
                                    key={column.id}
                                    sx={{
                                        flex: {
                                            xs: "0 0 100%",
                                            sm: "0 0 320px",
                                            lg: "0 0 25%",
                                        },
                                        minWidth: 0,
                                        height: "100%",
                                        scrollSnapAlign: "start",
                                    }}
                                >
                                    <FinderColumn
                                        embedded
                                        column={column}
                                        index={index}
                                        handleFileSelect={(file) =>
                                            props.onClose(file)
                                        }
                                        handleFolderSelect={
                                            handleFolderSelect
                                        }
                                        selectedColumnIndex={
                                            selectedColumnIndex
                                        }
                                    />
                                </Box>
                            ))}
                        </DragDropContext>
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    justifyContent: "space-between",
                }}
            >
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: { xs: "none", sm: "block" } }}
                >
                    {props.selectionHint || "Select a file to continue."}
                </Typography>
                <Button color="inherit" onClick={() => props.onClose()}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
}
